/**
 * Accounting API Client
 */
import { authService } from './auth';
import type {
  ChartOfAccount,
  ChartOfAccountFormData,
  JournalEntry,
  JournalEntryFormData,
  JournalEntryLine,
  AccountingPeriod,
  GeneralLedger,
  TrialBalance,
  BalanceSheet,
  ProfitLoss,
  AccountingDashboardData,
  AccountingReportParams,
  CustomerBalancesReport,
  CustomerDetailReport,
  AccountActivityReport,
  AuditLog,
  AuditReportParams,
  ApprovalWorkflow,
  UserRole,
  BankConnection,
  BankAccount,
  BankTransaction,
  BankRule,
  BankReconciliation,
  PlaidLinkToken,
  PlaidPublicTokenExchange,
  BankTransactionCategorize,
  CashFlowStatement,
  FinancialAnalyticsData,
  MonthEndChecklistItem,
  CreateYearPeriodsRequest,
  PeriodCloseRequest
} from '../types/accounting';

const API_BASE_URL = window.location.origin;

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

class AccountingApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = authService.getAccessToken();

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(options.headers as Record<string, string>),
    };

    const config: RequestInit = {
      headers,
      ...options,
    };

    const response = await fetch(url, config);

    if (response.status === 401) {
      // Try to refresh token
      try {
        const newAccessToken = await authService.refreshAccessToken();
        // Retry with new token
        config.headers = {
          ...headers,
          'Authorization': `Bearer ${newAccessToken}`
        };
        const retryResponse = await fetch(url, config);
        if (!retryResponse.ok) {
          throw new Error(`API Error: ${retryResponse.status}`);
        }
        return retryResponse.json();
      } catch (refreshError) {
        // Refresh failed, redirect to login
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Handle validation errors (400 status)
      if (response.status === 400 && errorData) {
        // Check for field-specific errors
        const fieldErrors = Object.entries(errorData)
          .filter(([key, value]) => key !== 'detail' && value)
          .map(([key, value]) => {
            const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const message = Array.isArray(value) ? value[0] : value;
            return `${fieldName}: ${message}`;
          });
        
        if (fieldErrors.length > 0) {
          throw new Error(fieldErrors[0]); // Show first field error
        }
      }
      throw new Error(errorData.detail || errorData.message || `API Error: ${response.status}`);
    }

    return response.json();
  }

  // Chart of Accounts endpoints
  async getChartOfAccounts(params?: {
    search?: string;
    account_type?: string;
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<ChartOfAccount>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.account_type) queryParams.append('account_type', params.account_type);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    
    return this.request<PaginatedResponse<ChartOfAccount>>(
      `/api/accounting/chart-of-accounts/?${queryParams}`
    );
  }

  async getAccount(id: number): Promise<ChartOfAccount> {
    return this.request<ChartOfAccount>(`/api/accounting/chart-of-accounts/${id}/`);
  }

  async createAccount(data: ChartOfAccountFormData): Promise<ChartOfAccount> {
    return this.request<ChartOfAccount>('/api/accounting/chart-of-accounts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccount(id: number, data: Partial<ChartOfAccountFormData>): Promise<ChartOfAccount> {
    return this.request<ChartOfAccount>(`/api/accounting/chart-of-accounts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAccount(id: number): Promise<void> {
    await this.request<void>(`/api/accounting/chart-of-accounts/${id}/`, {
      method: 'DELETE',
    });
  }

  async createDefaultAccounts(): Promise<{ message: string; created_count: number }> {
    return this.request('/api/accounting/chart-of-accounts/create_defaults/', {
      method: 'POST',
    });
  }

  // Journal Entry endpoints
  async getJournalEntries(params?: {
    search?: string;
    status?: string;
    entry_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<JournalEntry>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.entry_type) queryParams.append('entry_type', params.entry_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    
    return this.request<PaginatedResponse<JournalEntry>>(
      `/api/accounting/journal-entries/?${queryParams}`
    );
  }

  async getJournalEntry(id: number): Promise<JournalEntry> {
    return this.request<JournalEntry>(`/api/accounting/journal-entries/${id}/`);
  }

  async createJournalEntry(data: JournalEntryFormData): Promise<JournalEntry> {
    return this.request<JournalEntry>('/api/accounting/journal-entries/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateJournalEntry(id: number, data: Partial<JournalEntryFormData>): Promise<JournalEntry> {
    return this.request<JournalEntry>(`/api/accounting/journal-entries/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async postJournalEntry(id: number): Promise<JournalEntry> {
    return this.request<JournalEntry>(`/api/accounting/journal-entries/${id}/post/`, {
      method: 'POST',
    });
  }

  async voidJournalEntry(id: number, reason: string): Promise<JournalEntry> {
    return this.request<JournalEntry>(`/api/accounting/journal-entries/${id}/void/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async createReversingEntry(id: number): Promise<JournalEntry> {
    return this.request<JournalEntry>(`/api/accounting/journal-entries/${id}/reverse/`, {
      method: 'POST',
    });
  }

  // Journal Entry Lines
  async getJournalEntryLines(journalEntryId: number): Promise<JournalEntryLine[]> {
    return this.request<JournalEntryLine[]>(
      `/api/accounting/journal-entries/${journalEntryId}/lines/`
    );
  }

  // Accounting Periods
  async getAccountingPeriods(params?: {
    status?: string;
    period_type?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AccountingPeriod>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.period_type) queryParams.append('period_type', params.period_type);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    
    return this.request<PaginatedResponse<AccountingPeriod>>(
      `/api/accounting/periods/?${queryParams}`
    );
  }

  async getAccountingPeriod(id: number): Promise<AccountingPeriod> {
    return this.request<AccountingPeriod>(`/api/accounting/periods/${id}/`);
  }

  async closeAccountingPeriod(id: number): Promise<AccountingPeriod> {
    return this.request<AccountingPeriod>(`/api/accounting/periods/${id}/close/`, {
      method: 'POST',
    });
  }


  // General Ledger
  async getGeneralLedger(params?: {
    account?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<GeneralLedger>> {
    const queryParams = new URLSearchParams();
    if (params?.account) queryParams.append('account', String(params.account));
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    
    return this.request<PaginatedResponse<GeneralLedger>>(
      `/api/accounting/general-ledger/?${queryParams}`
    );
  }

  // Reports
  async getTrialBalance(params?: AccountingReportParams): Promise<{
    report_date: string;
    accounts: TrialBalance[];
    total_debits: number;
    total_credits: number;
  }> {
    const asOfDate = params?.as_of_date || new Date().toLocaleDateString('en-CA');
    const queryParams = new URLSearchParams({ as_of_date: asOfDate });
    const rows = await this.request<TrialBalance[]>(`/api/accounting/general-ledger/trial-balance/?${queryParams}`);
    const totals = rows.find(row => !row.account_number && row.account_name === 'TOTALS');
    const accounts = rows.filter(row => row !== totals);
    return { report_date: asOfDate, accounts,
      total_debits: Number(totals?.debit_balance ?? 0),
      total_credits: Number(totals?.credit_balance ?? 0) };
  }

  async getBalanceSheet(params?: AccountingReportParams): Promise<BalanceSheet> {
    const queryParams = new URLSearchParams();
    if (params?.as_of_date) queryParams.append('as_of_date', params.as_of_date);
    if (params?.show_zero_balances !== undefined) {
      queryParams.append('show_zero_balances', String(params.show_zero_balances));
    }
    
    return this.request<BalanceSheet>(`/api/accounting/reports/balance-sheet/?${queryParams}`);
  }

  async getProfitLoss(params?: AccountingReportParams): Promise<ProfitLoss> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.show_zero_balances !== undefined) {
      queryParams.append('show_zero_balances', String(params.show_zero_balances));
    }
    
    return this.request<ProfitLoss>(`/api/accounting/reports/profit-loss/?${queryParams}`);
  }

  async getCashFlow(params?: AccountingReportParams): Promise<CashFlowStatement> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    return this.request<CashFlowStatement>(`/api/accounting/reports/cash-flow/?${queryParams}`);
  }

  async exportCashFlow(params?: AccountingReportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    queryParams.append('format', 'xlsx');
    
    const accessToken = authService.getAccessToken();
    const response = await fetch(
      `${this.baseUrl}/api/accounting/reports/cash-flow/export/?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to export cash flow statement');
    }
    
    return response.blob();
  }

  async getAccountActivity(accountId: number, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{
    account: ChartOfAccount;
    transactions: GeneralLedger[];
    opening_balance: number;
    closing_balance: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    return this.request(
      `/api/accounting/reports/account-activity/${accountId}/?${queryParams}`
    );
  }

  // Dashboard
  async getDashboardData(): Promise<AccountingDashboardData> {
    return this.request<AccountingDashboardData>('/api/accounting/dashboard/');
  }

  // Financial Analytics
  async getFinancialAnalytics(params?: { end_date?: string }): Promise<FinancialAnalyticsData> {
    const queryParams = new URLSearchParams();
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    return this.request<FinancialAnalyticsData>(`/api/accounting/dashboard/financial_analytics/?${queryParams}`);
  }

  // A/R Aging Report
  async getARAgingReport(asOfDate?: string): Promise<any> {
    const queryParams = new URLSearchParams();
    if (asOfDate) queryParams.append('as_of_date', asOfDate);
    
    return this.request(`/api/accounting/reports/ar-aging/?${queryParams}`);
  }
  
  // A/P Aging Report
  async getAPAgingReport(asOfDate?: string): Promise<any> {
    const queryParams = new URLSearchParams();
    if (asOfDate) queryParams.append('as_of_date', asOfDate);
    
    return this.request(`/api/accounting/reports/ap-aging/?${queryParams}`);
  }

  // Customer Balances Report
  async getCustomerBalancesReport(asOfDate?: string): Promise<CustomerBalancesReport> {
    const queryParams = new URLSearchParams();
    if (asOfDate) queryParams.append('as_of_date', asOfDate);
    
    return this.request(`/api/accounting/report/customer-balances/?${queryParams}`);
  }

  // Customer Detail Report
  async getCustomerDetailReport(customerId: number): Promise<CustomerDetailReport> {
    const queryParams = new URLSearchParams();
    queryParams.append('customer_id', customerId.toString());
    
    return this.request(`/api/accounting/report/customer-detail/?${queryParams}`);
  }

  // Account Activity Report
  async getAccountActivityReport(
    accountId: number,
    startDate?: string,
    endDate?: string
  ): Promise<AccountActivityReport> {
    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId.toString());
    if (startDate) queryParams.append('start_date', startDate);
    if (endDate) queryParams.append('end_date', endDate);
    
    return this.request(`/api/accounting/report/account-activity/?${queryParams}`);
  }

  // Export functions
  async exportTrialBalance(params?: AccountingReportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.as_of_date) queryParams.append('as_of_date', params.as_of_date);
    queryParams.append('format', 'xlsx');
    
    const accessToken = authService.getAccessToken();
    const response = await fetch(
      `${this.baseUrl}/api/accounting/reports/trial-balance/export/?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to export trial balance');
    }
    
    return response.blob();
  }

  async exportBalanceSheet(params?: AccountingReportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.as_of_date) queryParams.append('as_of_date', params.as_of_date);
    queryParams.append('format', 'xlsx');
    
    const accessToken = authService.getAccessToken();
    const response = await fetch(
      `${this.baseUrl}/api/accounting/reports/balance-sheet/export/?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to export balance sheet');
    }
    
    return response.blob();
  }

  async exportProfitLoss(params?: AccountingReportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    queryParams.append('format', 'xlsx');
    
    const accessToken = authService.getAccessToken();
    const response = await fetch(
      `${this.baseUrl}/api/accounting/reports/profit-loss/export/?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to export profit & loss');
    }
    
    return response.blob();
  }

  // =====================================
  // Banking Integration Methods
  // =====================================

  // Bank Connections
  async getBankConnections(): Promise<PaginatedResponse<BankConnection>> {
    return this.request<PaginatedResponse<BankConnection>>('/api/accounting/bank-connections/');
  }

  async createLinkToken(): Promise<PlaidLinkToken> {
    return this.request<PlaidLinkToken>('/api/accounting/bank-connections/link-token/', {
      method: 'POST',
    });
  }

  async exchangePublicToken(data: PlaidPublicTokenExchange): Promise<BankConnection> {
    return this.request<BankConnection>('/api/accounting/bank-connections/exchange-token/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async syncBankConnection(id: number): Promise<{
    success: boolean;
    transactions_synced: number;
    last_synced: string;
  }> {
    return this.request(`/api/accounting/bank-connections/${id}/sync/`, {
      method: 'POST',
    });
  }

  async deleteBankConnection(id: number): Promise<void> {
    await this.request<void>(`/api/accounting/bank-connections/${id}/`, {
      method: 'DELETE',
    });
  }

  // Bank Accounts
  async getBankAccounts(params?: {
    connection?: number;
    is_active?: boolean;
  }): Promise<PaginatedResponse<BankAccount>> {
    const queryParams = new URLSearchParams();
    if (params?.connection) queryParams.append('connection', String(params.connection));
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    
    return this.request<PaginatedResponse<BankAccount>>(
      `/api/accounting/bank-accounts/?${queryParams}`
    );
  }

  async updateBankAccount(id: number, data: Partial<BankAccount>): Promise<BankAccount> {
    return this.request<BankAccount>(`/api/accounting/bank-accounts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Bank Transactions
  async getBankTransactions(params?: {
    account?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    is_pending?: boolean;
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<PaginatedResponse<BankTransaction>> {
    const queryParams = new URLSearchParams();
    if (params?.account) queryParams.append('account', String(params.account));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.is_pending !== undefined) queryParams.append('is_pending', String(params.is_pending));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    if (params?.search) queryParams.append('search', params.search);
    
    return this.request<PaginatedResponse<BankTransaction>>(
      `/api/accounting/bank-transactions/?${queryParams}`
    );
  }

  async categorizeBankTransactions(data: BankTransactionCategorize): Promise<{
    success: boolean;
    transactions_updated: number;
    journal_entries_created: number[];
  }> {
    return this.request('/api/accounting/bank-transactions/bulk-categorize/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Bank Rules
  async getBankRules(params?: {
    is_active?: boolean;
  }): Promise<PaginatedResponse<BankRule>> {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    
    return this.request<PaginatedResponse<BankRule>>(
      `/api/accounting/bank-rules/?${queryParams}`
    );
  }

  async createBankRule(data: Omit<BankRule, 'id' | 'created_at' | 'updated_at'>): Promise<BankRule> {
    return this.request<BankRule>('/api/accounting/bank-rules/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBankRule(id: number, data: Partial<BankRule>): Promise<BankRule> {
    return this.request<BankRule>(`/api/accounting/bank-rules/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBankRule(id: number): Promise<void> {
    await this.request<void>(`/api/accounting/bank-rules/${id}/`, {
      method: 'DELETE',
    });
  }

  async applyBankRule(id: number): Promise<{
    success: boolean;
    transactions_matched: number;
    journal_entries_created: number[];
  }> {
    return this.request(`/api/accounting/bank-rules/${id}/apply-to-transactions/`, {
      method: 'POST',
    });
  }

  // Bank Reconciliations
  async getBankReconciliations(params?: {
    account?: number;
    status?: string;
  }): Promise<PaginatedResponse<BankReconciliation>> {
    const queryParams = new URLSearchParams();
    if (params?.account) queryParams.append('account', String(params.account));
    if (params?.status) queryParams.append('status', params.status);
    
    return this.request<PaginatedResponse<BankReconciliation>>(
      `/api/accounting/bank-reconciliations/?${queryParams}`
    );
  }

  async createBankReconciliation(data: Omit<BankReconciliation, 'id' | 'created_at' | 'updated_at'>): Promise<BankReconciliation> {
    return this.request<BankReconciliation>('/api/accounting/bank-reconciliations/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async completeBankReconciliation(id: number, force?: boolean): Promise<BankReconciliation> {
    return this.request<BankReconciliation>(`/api/accounting/bank-reconciliations/${id}/complete/`, {
      method: 'POST',
      body: JSON.stringify({ force: force || false }),
    });
  }

  async getReconciliationTransactions(reconciliationId: number): Promise<BankTransaction[]> {
    return this.request<BankTransaction[]>(
      `/api/accounting/bank-reconciliations/${reconciliationId}/transactions/`
    );
  }

  async clearTransaction(reconciliationId: number, transactionId: number): Promise<BankReconciliation> {
    return this.request<BankReconciliation>(
      `/api/accounting/bank-reconciliations/${reconciliationId}/clear-transaction/`, {
        method: 'POST',
        body: JSON.stringify({ transaction_id: transactionId }),
      }
    );
  }

  async unclearTransaction(reconciliationId: number, transactionId: number): Promise<BankReconciliation> {
    return this.request<BankReconciliation>(
      `/api/accounting/bank-reconciliations/${reconciliationId}/unclear-transaction/`, {
        method: 'POST',
        body: JSON.stringify({ transaction_id: transactionId }),
      }
    );
  }

  async undoReconciliation(id: number): Promise<BankReconciliation> {
    return this.request<BankReconciliation>(`/api/accounting/bank-reconciliations/${id}/undo/`, {
      method: 'POST',
    });
  }

  async deleteReconciliation(id: number): Promise<void> {
    await this.request<void>(`/api/accounting/bank-reconciliations/${id}/`, {
      method: 'DELETE',
    });
  }

  async getUnmatchedTransactions(reconciliationId: number): Promise<BankTransaction[]> {
    return this.request<BankTransaction[]>(
      `/api/accounting/bank-reconciliations/${reconciliationId}/unmatched-transactions/`
    );
  }

  // Period Management
  async getPeriods(params?: {
    status?: string;
    period_type?: string;
    is_year_end?: boolean;
  }): Promise<AccountingPeriod[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.period_type) queryParams.append('period_type', params.period_type);
    if (params?.is_year_end !== undefined) queryParams.append('is_year_end', String(params.is_year_end));
    
    return this.request<AccountingPeriod[]>(`/api/accounting/periods/?${queryParams}`);
  }

  async getPeriod(id: number): Promise<AccountingPeriod> {
    return this.request<AccountingPeriod>(`/api/accounting/periods/${id}/`);
  }

  async getCurrentPeriod(date?: string): Promise<AccountingPeriod> {
    const queryParams = new URLSearchParams();
    if (date) queryParams.append('date', date);
    
    return this.request<AccountingPeriod>(`/api/accounting/periods/current/?${queryParams}`);
  }

  async createYearPeriods(data: CreateYearPeriodsRequest): Promise<{
    message: string;
    periods: AccountingPeriod[];
  }> {
    return this.request(`/api/accounting/periods/create-year/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async closePeriod(id: number, data: PeriodCloseRequest): Promise<AccountingPeriod> {
    return this.request<AccountingPeriod>(`/api/accounting/periods/${id}/close/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPeriodChecklist(periodId: number): Promise<MonthEndChecklistItem[]> {
    return this.request<MonthEndChecklistItem[]>(`/api/accounting/periods/${periodId}/checklist/`);
  }

  // Month-End Checklist
  async getChecklist(params?: {
    period?: number;
    item_type?: string;
    is_completed?: boolean;
    is_required?: boolean;
  }): Promise<MonthEndChecklistItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', String(params.period));
    if (params?.item_type) queryParams.append('item_type', params.item_type);
    if (params?.is_completed !== undefined) queryParams.append('is_completed', String(params.is_completed));
    if (params?.is_required !== undefined) queryParams.append('is_required', String(params.is_required));
    
    return this.request<MonthEndChecklistItem[]>(`/api/accounting/checklist/?${queryParams}`);
  }

  async completeChecklistItem(id: number, data?: { notes?: string }): Promise<MonthEndChecklistItem> {
    return this.request<MonthEndChecklistItem>(`/api/accounting/checklist/${id}/complete/`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async uncompleteChecklistItem(id: number): Promise<MonthEndChecklistItem> {
    return this.request<MonthEndChecklistItem>(`/api/accounting/checklist/${id}/uncomplete/`, {
      method: 'POST',
    });
  }

  async getChecklistByPeriod(): Promise<Array<{
    period: AccountingPeriod;
    checklist_summary: {
      total: number;
      completed: number;
      required: number;
      required_completed: number;
      can_close: boolean;
    };
  }>> {
    return this.request(`/api/accounting/checklist/by-period/`);
  }

  // Audit Trail Methods
  async getAuditLogs(params?: AuditReportParams): Promise<AuditLog[]> {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.model_type) queryParams.append('model_type', params.model_type);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.risk_level) queryParams.append('risk_level', params.risk_level);
    if (params?.requires_review !== undefined) queryParams.append('requires_review', String(params.requires_review));
    
    return this.request<AuditLog[]>(`/api/accounting/audit-logs/?${queryParams}`);
  }

  async getAuditLogById(id: number): Promise<AuditLog> {
    return this.request<AuditLog>(`/api/accounting/audit-logs/${id}/`);
  }

  async getAuditLogsByObject(modelType: string, objectId: string): Promise<AuditLog[]> {
    return this.request<AuditLog[]>(`/api/accounting/audit-logs/by_object/?model_type=${modelType}&object_id=${objectId}`);
  }

  async getHighRiskAuditLogs(): Promise<AuditLog[]> {
    return this.request<AuditLog[]>('/api/accounting/audit-logs/high_risk/');
  }

  async markAuditLogReviewed(id: number): Promise<AuditLog> {
    return this.request<AuditLog>(`/api/accounting/audit-logs/${id}/mark_reviewed/`, {
      method: 'POST',
    });
  }

  async getAuditSummary(days: number = 30): Promise<any> {
    return this.request(`/api/accounting/audit-logs/summary/?days=${days}`);
  }

  async exportAuditLogs(params: AuditReportParams): Promise<Blob> {
    const accessToken = localStorage.getItem('access_token');
    const response = await fetch('/api/accounting/audit-logs/export/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      },
      body: JSON.stringify(params),
    });
    
    if (!response.ok) {
      throw new Error('Failed to export audit logs');
    }
    
    return response.blob();
  }

  // Approval Workflow Methods
  async getApprovals(params?: {
    status?: string;
    workflow_type?: string;
    my_pending?: boolean;
    requested_by?: number;
  }): Promise<ApprovalWorkflow[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.workflow_type) queryParams.append('workflow_type', params.workflow_type);
    if (params?.my_pending) queryParams.append('my_pending', 'true');
    if (params?.requested_by) queryParams.append('requested_by', params.requested_by.toString());
    
    return this.request<ApprovalWorkflow[]>(`/api/accounting/approvals/?${queryParams}`);
  }

  async getApprovalById(id: number): Promise<ApprovalWorkflow> {
    return this.request<ApprovalWorkflow>(`/api/accounting/approvals/${id}/`);
  }

  async approveWorkflow(id: number, notes?: string): Promise<ApprovalWorkflow> {
    return this.request<ApprovalWorkflow>(`/api/accounting/approvals/${id}/approve/`, {
      method: 'POST',
      body: JSON.stringify({ action: 'approve', notes }),
    });
  }

  async rejectWorkflow(id: number, notes?: string): Promise<ApprovalWorkflow> {
    return this.request<ApprovalWorkflow>(`/api/accounting/approvals/${id}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ action: 'reject', notes }),
    });
  }

  async getPendingApprovals(): Promise<ApprovalWorkflow[]> {
    return this.request<ApprovalWorkflow[]>('/api/accounting/approvals/pending/');
  }

  async getMyApprovalRequests(): Promise<ApprovalWorkflow[]> {
    return this.request<ApprovalWorkflow[]>('/api/accounting/approvals/my_requests/');
  }

  async getApprovalStatistics(): Promise<any> {
    return this.request('/api/accounting/approvals/statistics/');
  }

  // User Role Methods
  async getUserRoles(): Promise<UserRole[]> {
    return this.request<UserRole[]>('/api/accounting/user-roles/');
  }

  async getUserRoleById(id: number): Promise<UserRole> {
    return this.request<UserRole>(`/api/accounting/user-roles/${id}/`);
  }

  async createUserRole(data: Partial<UserRole>): Promise<UserRole> {
    return this.request<UserRole>('/api/accounting/user-roles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserRole(id: number, data: Partial<UserRole>): Promise<UserRole> {
    return this.request<UserRole>(`/api/accounting/user-roles/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async assignRole(userId: number, role: string, options?: {
    valid_from?: string;
    valid_until?: string;
    max_transaction_amount?: number;
  }): Promise<UserRole> {
    return this.request<UserRole>('/api/accounting/user-roles/assign_role/', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        role,
        ...options,
      }),
    });
  }

  async deactivateRole(id: number): Promise<UserRole> {
    return this.request<UserRole>(`/api/accounting/user-roles/${id}/deactivate/`, {
      method: 'POST',
    });
  }

  async activateRole(id: number): Promise<UserRole> {
    return this.request<UserRole>(`/api/accounting/user-roles/${id}/activate/`, {
      method: 'POST',
    });
  }

  async getMyRole(): Promise<UserRole> {
    return this.request<UserRole>('/api/accounting/user-roles/my_role/');
  }

  async getPermissionsMatrix(): Promise<any[]> {
    return this.request('/api/accounting/user-roles/permissions_matrix/');
  }
}

export const apiAccountingClient = new AccountingApiClient();
